import { useCallback } from 'react';

import { IClientTeam } from '~/services/api/clientDetails/types';
import { IDictionary } from '~/services/api/dictionaries/types';
import useRole from '~/store/user/hooks/useRole';

type BaseTeamType = {
  isPrimary: boolean;
};

type ClientTeamType = IClientTeam | (IDictionary & BaseTeamType);

const hasTeamExtendedInfo = (team: ClientTeamType): team is IClientTeam => 'team' in team;

interface IUseClientMultiTeam {
  getClientMultiTeam: (client: ClientTeamType[]) => ClientTeamType;
  getClientMultiTeamId: (client: ClientTeamType[]) => number;
}

const useClientMultiTeam = (): IUseClientMultiTeam => {
  const {
    isGlobalUser = false,
    isLocalAdmin = false,
    isProgramAssistant = false,
    team,
    role: { teams: userAvailableTeams },
  } = useRole();

  const getClientMultiTeam = useCallback(
    (clientTeams: ClientTeamType[]): ClientTeamType => {
      if (!clientTeams?.length) {
        return null;
      }

      const primaryTeam = clientTeams.find(t => t.isPrimary) || clientTeams?.[0];
      const availableTeam = clientTeams.find(
        t =>
          !userAvailableTeams.length ||
          userAvailableTeams.some(ct =>
            hasTeamExtendedInfo(t) ? t.team.id === ct.id : t.id === ct.id,
          ),
      );

      return isGlobalUser || isLocalAdmin || isProgramAssistant || availableTeam
        ? availableTeam || primaryTeam
        : clientTeams.find(item =>
            hasTeamExtendedInfo(item) ? item.team.id === team?.id : item.id === team?.id,
          ) || primaryTeam;
    },
    [isGlobalUser, isLocalAdmin, isProgramAssistant, team?.id, userAvailableTeams],
  );

  const getClientMultiTeamId = useCallback(
    (clientTeams: ClientTeamType[]): number => {
      const clientTeam = getClientMultiTeam(clientTeams);
      if (!clientTeam) {
        return null;
      }

      return hasTeamExtendedInfo(clientTeam) ? clientTeam.team.id : clientTeam.id;
    },
    [getClientMultiTeam],
  );

  return { getClientMultiTeam, getClientMultiTeamId };
};

export default useClientMultiTeam;
